import { PostgrestError } from '@supabase/supabase-js';

// Error types
export type ErrorType = 
  | 'AUTH_ERROR'
  | 'NETWORK_ERROR'
  | 'DATABASE_ERROR'
  | 'VALIDATION_ERROR'
  | 'PERMISSION_ERROR'
  | 'NOT_FOUND'
  | 'UNKNOWN_ERROR';

// Error messages mapping
const errorMessages: Record<ErrorType, string> = {
  AUTH_ERROR: 'Erro de autenticação. Verifique suas credenciais.',
  NETWORK_ERROR: 'Erro de conexão. Verifique sua internet.',
  DATABASE_ERROR: 'Erro ao acessar o banco de dados.',
  VALIDATION_ERROR: 'Dados inválidos. Verifique as informações.',
  PERMISSION_ERROR: 'Você não tem permissão para realizar esta ação.',
  NOT_FOUND: 'O recurso solicitado não foi encontrado.',
  UNKNOWN_ERROR: 'Ocorreu um erro inesperado. Tente novamente.',
};

// Supabase error codes mapping
const supabaseErrorCodes: Record<string, ErrorType> = {
  'PGRST301': 'DATABASE_ERROR',
  'PGRST302': 'PERMISSION_ERROR',
  'PGRST404': 'NOT_FOUND',
  'auth/invalid-email': 'VALIDATION_ERROR',
  'auth/wrong-password': 'AUTH_ERROR',
  'auth/user-not-found': 'AUTH_ERROR',
  'auth/email-already-in-use': 'VALIDATION_ERROR',
};

// Custom error messages for specific cases
const customErrorMessages: Record<string, string> = {
  'auth/invalid-email': 'Email inválido.',
  'auth/wrong-password': 'E-mail ou senha incorretos!',
  'auth/user-not-found': 'E-mail ou senha incorretos!',
  'auth/email-already-in-use': 'Este email já está em uso.',
  'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres.',
  'auth/invalid-credential': 'E-mail ou senha incorretos!',
  'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
  'Invalid login credentials': 'E-mail ou senha incorretos!',
  '23505': 'Este registro já existe.',
  '23503': 'Não é possível excluir este registro pois está sendo usado.',
  '42P01': 'Erro interno do banco de dados.',
};

// Function to get error message
export function getErrorMessage(error: unknown): string {
  // Handle network errors
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return errorMessages.NETWORK_ERROR;
  }

  // Handle Supabase PostgrestError
  if (isPostgrestError(error)) {
    // Check for custom message first
    if (error.code && customErrorMessages[error.code]) {
      return customErrorMessages[error.code];
    }

    // Then check for mapped error type
    if (error.code && supabaseErrorCodes[error.code]) {
      return errorMessages[supabaseErrorCodes[error.code]];
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    // Check for custom message first
    const errorCode = extractErrorCode(error.message);
    if (errorCode && customErrorMessages[errorCode]) {
      return customErrorMessages[errorCode];
    }

    // Check for exact message match
    if (customErrorMessages[error.message]) {
      return customErrorMessages[error.message];
    }

    // Check for mapped error type
    if (errorCode && supabaseErrorCodes[errorCode]) {
      return errorMessages[supabaseErrorCodes[errorCode]];
    }

    // Return cleaned up error message if no mapping found
    return cleanErrorMessage(error.message);
  }

  // Default error message
  return errorMessages.UNKNOWN_ERROR;
}

// Helper function to check if error is PostgrestError
function isPostgrestError(error: any): error is PostgrestError {
  return error && typeof error === 'object' && 'code' in error;
}

// Helper function to extract error code from message
function extractErrorCode(message: string): string | null {
  const codeMatch = message.match(/\[(.*?)\]/);
  return codeMatch ? codeMatch[1] : null;
}

// Helper function to clean up error message
function cleanErrorMessage(message: string): string {
  // Remove technical details and codes
  message = message.replace(/\[.*?\]/g, '').trim();
  message = message.replace(/Error:/i, '').trim();
  
  // Capitalize first letter
  return message.charAt(0).toUpperCase() + message.slice(1);
}

// Function to handle form validation errors
export function getValidationErrorMessage(fieldName: string): string {
  const fieldMessages: Record<string, string> = {
    email: 'Email inválido',
    password: 'Senha inválida',
    name: 'Nome inválido',
    phone: 'Telefone inválido',
    title: 'Título inválido',
    description: 'Descrição inválida',
    price: 'Preço inválido',
    area: 'Área inválida',
    address: 'Endereço inválido',
    city: 'Cidade inválida',
    state: 'Estado inválido',
  };

  return fieldMessages[fieldName] || 'Campo inválido';
}